import { UUID } from 'crypto';
import { isDateAfter, isDateBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import {
    mapStowagePlanChangeToData,
    mapStowagePlanChangeToStowagePlanDataItem,
    sortStowagePlanRotationPortCalls,
    StowageDataItem,
} from 'src/factory/Stowage/StowageOperationFactory';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { getStowageChangeKey } from './stowageChangeDataTypeUtils';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { mergeNestedObjects } from 'src/utils/propertyUtils';

export const getStowageDataItems = (stowagePlanChanges: StowagePlanChange[], stowagePlans: StowagePlan[], stowagePlan: StowagePlan) => {
    const _stowageDataItems = buildStowagePlanDataItems(stowagePlanChanges, stowagePlans, stowagePlan.stowagePlanGuid);
    const _violations: Violation[] = [];
    const _stowagePlanRotationPortCallsMap: { [key: string]: RotationPortCall } = {};
    _stowageDataItems.forEach((stowageDataItem) => {
        if (stowageDataItem.cargo) updateCargo(stowagePlan, stowageDataItem.cargo);
        if (stowageDataItem.stowageItem) stowagePlan.stowageItems.push(stowageDataItem.stowageItem);
        if (stowageDataItem.adjustedCargoTemperature) stowagePlan.adjustedCargoTemperatures.push(stowageDataItem.adjustedCargoTemperature);
        if (stowageDataItem.blockedTank) stowagePlan.blockedTanks.push(stowageDataItem.blockedTank);
        if (stowageDataItem.violation) _violations.push(stowageDataItem.violation);
        if (stowageDataItem.portCall) updatePortCall(_stowagePlanRotationPortCallsMap, stowageDataItem.portCall);
        if (stowageDataItem.berthCall) updateBerthCall(_stowagePlanRotationPortCallsMap, stowageDataItem.berthCall);
        if (stowageDataItem.cargoOperation) updateCargoOperation(_stowagePlanRotationPortCallsMap, stowageDataItem.cargoOperation);
    });

    updateCargoInCargoOperations(_stowagePlanRotationPortCallsMap, stowagePlan);
    const deduplicatedPortCalls = deduplicatePortCallsByImosId(Object.values(_stowagePlanRotationPortCallsMap));
    return {
        updatedStowagePlan: stowagePlan,
        violations: _violations,
        stowagePlanRotationPortCalls: sortStowagePlanRotationPortCalls(deduplicatedPortCalls),
    };
};

/**
 * When a parent plan and child plan both contain port call changes for the same IMOS port call
 * but with different changeDataGuids (e.g. after IMOS rotation restructure), buildStowagePlanChangesTree
 * collects both. Since groupByChangeDataGuid keys on the GUID, both survive as separate entries.
 * This function deduplicates by imosPortCallId, keeping the entry that was added last (latest portCallGuid).
 */
const deduplicatePortCallsByImosId = (portCalls: RotationPortCall[]): RotationPortCall[] => {
    const byImosId = new Map<number, RotationPortCall[]>();
    const result: RotationPortCall[] = [];

    for (const pc of portCalls) {
        if (pc.imosPortCallId != null) {
            const existing = byImosId.get(pc.imosPortCallId);
            if (existing) {
                existing.push(pc);
            } else {
                byImosId.set(pc.imosPortCallId, [pc]);
            }
        } else {
            // Port calls without imosPortCallId (OrcaWeb-created) are always kept
            result.push(pc);
        }
    }

    for (const [, duplicates] of byImosId) {
        // Keep the last entry — child plan changes are appended after parent changes,
        // so the last one represents the most recent version of this port call.
        result.push(duplicates[duplicates.length - 1]);
    }

    return result;
};

const buildStowagePlanDataItems = (stowagePlanChanges: StowagePlanChange[], stowagePlans: StowagePlan[], stowagePlanGuid: UUID) => {
    const stowagePlanChangesTree = buildStowagePlanChangesTree(stowagePlanChanges, stowagePlans, stowagePlanGuid);
    const _stowagePlanChangesByChangeDataGuid = groupByChangeDataGuid(stowagePlanChangesTree);
    return Object.keys(_stowagePlanChangesByChangeDataGuid)
        .filter((key) => !_stowagePlanChangesByChangeDataGuid[key][_stowagePlanChangesByChangeDataGuid[key].length - 1].isDeleted)
        .map((key) => {
            const changes = _stowagePlanChangesByChangeDataGuid[key].reduce((acc, spc) => {
                if (!acc.changeDataGuid) {
                    acc = mapStowagePlanChangeToStowagePlanDataItem(spc);
                } else {
                    const data = mapStowagePlanChangeToData(spc.data);

                    const changeKey = getStowageChangeKey(spc.changeDataType);
                    if (changeKey) {
                        // Check if existing value and new data are objects that need to be merged
                        if (acc[changeKey] && typeof acc[changeKey] === 'object' && data && typeof data === 'object') {
                            // Create a new object with merged properties
                            const mergedData = mergeNestedObjects(acc[changeKey], data);
                            acc[changeKey] = { ...mergedData, changeDataGuid: spc.changeDataGuid };
                        } else {
                            acc[changeKey] = { ...acc[changeKey], ...data, changeDataGuid: spc.changeDataGuid };
                        }
                    }
                }
                if (acc.isDeleted) {
                    acc.isDeleted = false;
                }

                return acc;
            }, {} as StowageDataItem);

            return changes;
        });
};

const buildStowagePlanChangesTree = (stowagePlanChanges: StowagePlanChange[], stowagePlans: StowagePlan[], stowagePlanGuid: UUID) => {
    const findStowagePlan = (id) => stowagePlans.find((plan) => plan.stowagePlanGuid === id);

    let firstSyncedUntil = null;

    const collectChanges = (planId, syncedUntil = null): StowagePlanChange[] => {
        const plan = findStowagePlan(planId);
        if (!plan) return [];

        if (!firstSyncedUntil && syncedUntil) {
            firstSyncedUntil = syncedUntil;
        }
        if (firstSyncedUntil && syncedUntil && isDateAfter(syncedUntil, firstSyncedUntil)) {
            syncedUntil = firstSyncedUntil;
        }

        const changes = stowagePlanChanges.filter(
            (change) => change.stowagePlanGuid === planId && (syncedUntil === null || isDateBefore(change.lastUpdatedUtc, syncedUntil))
        );

        if (plan.parentGuid) {
            const parentPlan = findStowagePlan(plan.parentGuid);
            if (parentPlan) {
                const parentChanges = collectChanges(plan.parentGuid, plan.syncedWithParentDateTimeUtc);
                return [...parentChanges, ...changes];
            }
        }

        return changes;
    };

    return collectChanges(stowagePlanGuid);
};

const groupByChangeDataGuid = (stowagePlanChanges: StowagePlanChange[]) => {
    const groups = stowagePlanChanges
        .sort((a, b) => a.changeDataType - b.changeDataType)
        .reduce((acc, spc) => {
            if (!acc[spc.changeDataGuid]) {
                acc[spc.changeDataGuid] = [];
            }
            acc[spc.changeDataGuid].push(spc);
            return acc;
        }, {} as { [key: string]: StowagePlanChange[] });

    Object.keys(groups).forEach((key) => {
        groups[key] = groups[key].sort((a, b) => {
            const aDate = a.lastUpdatedUtc;
            const bDate = b.lastUpdatedUtc;
            if (!aDate && !bDate) return 0;
            if (!aDate) return -1; // Changes without a date (initial creation) sort first
            if (!bDate) return 1;
            return isDateBefore(aDate, bDate) ? -1 : 1;
        });
    });

    return groups;
};

const updateCargo = (stowagePlan: StowagePlan, cargo: RotationCargo) => {
    stowagePlan.cargoes.push(cargo);
};

const updateCargoInCargoOperations = (_stowagePlanRotationPortCallsMap: { [key: string]: RotationPortCall }, stowagePlan: StowagePlan) => {
    const cargoByGuid = new Map<string, RotationCargo>();
    for (const cargo of stowagePlan.cargoes) {
        if (cargo.cargoGuid && !cargoByGuid.has(cargo.cargoGuid)) {
            cargoByGuid.set(cargo.cargoGuid, cargo);
        }
    }

    for (const portCallGuid in _stowagePlanRotationPortCallsMap) {
        const portCall = _stowagePlanRotationPortCallsMap[portCallGuid];
        if (portCall.berthCalls) {
            for (const berthCall of portCall.berthCalls) {
                if (berthCall.cargoOperations) {
                    for (const cargoOperation of berthCall.cargoOperations) {
                        if (!cargoOperation.cargo) {
                            cargoOperation.cargo = cargoByGuid.get(cargoOperation.cargoGuid);
                        }
                    }
                }
            }
        }
    }
};

const updatePortCall = (_stowagePlanRotationPortCallsMap: { [key: string]: RotationPortCall }, portCall: RotationPortCall) => {
    if (portCall.portCallGuid) {
        _stowagePlanRotationPortCallsMap[portCall.portCallGuid] = portCall;
    } else {
        if (!_stowagePlanRotationPortCallsMap[portCall.portCallGuid]) {
            _stowagePlanRotationPortCallsMap[portCall.portCallGuid] = portCall;
        }
    }
};

const updateBerthCall = (_stowagePlanRotationPortCallsMap: { [key: string]: RotationPortCall }, berthCall: RotationBerthCall) => {
    const _portCall = _stowagePlanRotationPortCallsMap[berthCall.portCallGuid];
    if (_portCall) {
        _portCall.berthCalls = _portCall.berthCalls || [];
        berthCall.portCall = _portCall;
        if (!_portCall.berthCalls.some((b) => b.berthCallGuid === berthCall.berthCallGuid)) {
            _portCall.berthCalls.push(berthCall);
        }
    }
};
const updateCargoOperation = (_stowagePlanRotationPortCallsMap: { [key: string]: RotationPortCall }, cargoOperation: RotationCargoOperation) => {
    const _portCall = _stowagePlanRotationPortCallsMap[cargoOperation.portCallGuid];

    if (_portCall) {
        const _berthCall = _portCall.berthCalls.find((b) => b.berthCallGuid === cargoOperation.berthCallGuid);
        if (_berthCall) {
            _berthCall.cargoOperations = _berthCall.cargoOperations || [];
            if (!_berthCall.cargoOperations.some((c) => c.cargoOperationGuid === cargoOperation.cargoOperationGuid)) {
                _berthCall.cargoOperations.push(cargoOperation);
            }
        }
    }
};
